import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ContentLayout from '@layouts/ContentLayout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  Grid,
  Heading,
  HStack,
  StackDivider,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getIfRFIEnabled, getRFIDetails } from '@store/actions/rfiActions';
import FullPageSpinner from '@components/FullPageSpinner';
import { RFI_CONSTANTS as types } from '@store/constants';
import ModalLayout from '@components/Modal/ModalLayout';
import { CARD_TYPE, ROLES } from '@constants';
import GenericCard from '@components/GenericCard';
import { RequestedItem } from '@components/RFI/RequestedItem';
import { FaCheck, FaRedo } from 'react-icons/fa';
import {
  RFI_ITEMS,
  RFI_MODAL_MODE,
  RFI_REQUEST_REASONS,
  RFI_STATUS,
  RFI_TYPES,
  SOURCE_TENANTS_ALLOWED_TO_UPDATE_BENEFICIARIES
} from './constants';
import UpdateModal from '@components/RFI/UpdateModal';
import RFIEntryModal from '@components/RFI/RFIEntryModal';
import CopyableText from '@components/CopyableText';
import { MdCancel, MdDriveFileRenameOutline } from 'react-icons/md';
import DynamicInputModal from '@components/DynamicInputModal';
import { beneficiaryConfig as config } from '@views/User/Beneficiaries/utils';
import OrderDetailsCard from '@components/OrderDetailsCard';
import useAuthRole from '@hooks/useAuthRole';
import { rfiKycConfig } from '@views/RFITransactions/utils';


const RFIDetails = () => {
  const { hasAnyAuthority } = useAuthRole();
  const dispatch = useDispatch();
  const { orderId, rfiType, userId } = useParams();

  const [searchParams] = useSearchParams();
  const orderResource = searchParams.get('orderResource');

  const rfiList = useSelector((state) => state.rfiDetails.availableList);
  const rfiEnabled = useSelector((state) => state.rfiDetails.rfiEnabled);
  const { data, isLoading } = useSelector((state) => state.rfiDetails.rfi);

  const [updatedRequestedItems, setUpdatedRequestedItems] = useState([]);
  const [aggregateStatus, setAggregateStatus] = useState(false);
  const [markAsFailed, setMarkAsFailed] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  const {
    isOpen: isCreateRFIOpen,
    onOpen: onCreateRFIOpen,
    onClose: onCreateRFIClose
  } = useDisclosure();

  const {
    isOpen: isReopenRFIModalOpen,
    onOpen: onReopenRFIModalOpen,
    onClose: onReopenRFIModalClose
  } = useDisclosure();

  const {
    isOpen: isSaveRFIOpen,
    onOpen: onSaveRFIOpen,
    onClose: onSaveRFIClose
  } = useDisclosure();

  const {
    isOpen: isUpdateBeneficiaryModalOpen,
    onOpen: onUpdateBeneficiaryModalOpen,
    onClose: onUpdateBeneficiaryModalClose
  } = useDisclosure();

  const {
    isOpen: isKycUpdateOpen,
    onOpen: onKycUpdateOpen,
    onClose: onKycUpdateClose
  } = useDisclosure();

  useEffect(() => {
    if (orderId && rfiType && rfiType !== 'new') {
      dispatch(
        getRFIDetails({
          orderId,
          rfiType: rfiType,
          onSuccess: (data) => {
            dispatch({
              type: types.GET_RFI_DETAILS_SUCCESS,
              payload: {
                data: data
              }
            });
            setUpdatedRequestedItems(data.data?.requestedItems || []);
          }
        })
      );
    }
    if (orderId && userId && rfiType && rfiType === 'new') {
      dispatch(
        getIfRFIEnabled(userId, (data) => {
          dispatch({
            type: types.IF_USER_RFI_ENABLED_SUCCESS,
            payload: {
              data: data?.data
            }
          });
        })
      );
    }
  }, [orderId, rfiType, userId]);

  const isUserKycNameUpdatable = (data) => {
    return data
      && data.status === RFI_STATUS.SUBMITTED
      && data.requestReason === RFI_REQUEST_REASONS.REQUESTED_BY_PROVIDER
      && SOURCE_TENANTS_ALLOWED_TO_UPDATE_BENEFICIARIES.includes(data.sourceTenant);
  }

  const getAggregateRFIStatus = () => {
    if (markAsFailed) return RFI_STATUS.FAILED;
    if (aggregateStatus) return RFI_STATUS.REJECTED;
    if (data?.requestReason === RFI_REQUEST_REASONS.REQUESTED_BY_PROVIDER) {
      return RFI_STATUS.SENT_TO_PROVIDER;
    }
    return RFI_STATUS.VERIFIED;
  };

  const handleOnSuccessUpdateCleanUp = useCallback(() => {
    setSelectedBeneficiary(null);
    onUpdateBeneficiaryModalClose();
  }, []);

  const handleUpdateBeneficiaryModalSubmit = (formData) => {
    dispatch(
      config.updateBeneficiaryDetails({
        userId,
        beneficiaryId: selectedBeneficiary.beneficiaryId,
        payload: config.updateBeneficiaryDetailsPayloadConverter({
          beneficiaryData: selectedBeneficiary,
          formData
        }),
        onSuccess: () => {
          handleOnSuccessUpdateCleanUp()
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    )
  }

  const handleUpdateKycNameModalSubmit = (formData) => {
    dispatch(
      rfiKycConfig.updateUserKycName({
        userId,
        ...rfiKycConfig.userKycNamePayloadConverter({
          formData
        }),
        onSuccess: () => {
          handleOnSuccessUpdateCleanUp()
        },
        onComplete: () => {
          setIsModalLoading(false);
        }
      })
    )
  }

  const handleAccept = () => {
    setUpdatedRequestedItems((prevState) =>
      prevState.map((obj) => {
        const newObj = { ...obj };
        const updatedValue = { ...obj.value };
        if (obj.value.inputType === RFI_ITEMS.TEXT) {
          if (
            (obj.value.frontStatus || obj.value.status) === RFI_STATUS.SUBMITTED
          ) {
            updatedValue.status = RFI_STATUS.VERIFIED;
            updatedValue.frontStatus = RFI_STATUS.VERIFIED;
            newObj.frontStatus = RFI_STATUS.VERIFIED;
          }
        } else if (obj.value.inputType === RFI_ITEMS.DOCUMENT) {
          if (obj.value.frontStatus === RFI_STATUS.SUBMITTED) {
            updatedValue.frontStatus = RFI_STATUS.VERIFIED;
            newObj.frontStatus = RFI_STATUS.VERIFIED;
          }
          if (obj.value.backStatus === RFI_STATUS.SUBMITTED) {
            updatedValue.backStatus = RFI_STATUS.VERIFIED;
            newObj.backStatus = RFI_STATUS.VERIFIED;
          }
        }
        return { ...newObj, value: updatedValue };
      })
    );
    onSaveRFIOpen();
    setMarkAsFailed(false);
  };

  const handleFail = () => {
    setAggregateStatus(true);
    setMarkAsFailed(true);
    onSaveRFIOpen();
  };

  // Note :: Keeping track of current requested RFI's
  const disabledFieldSet = useMemo(() => {
    if (!Array.isArray(updatedRequestedItems)) return new Set();
    return new Set(
      updatedRequestedItems.map(
        (item) => `${item?.requestedFor}-${item?.requestedInfo}`
      )
    );
  }, [updatedRequestedItems]);

  if (isLoading || rfiEnabled.isLoading) return <FullPageSpinner />;

  return (
    <ContentLayout>
      {/*Create RFI Modal*/}
      <ModalLayout
        size="lg"
        isOpen={isCreateRFIOpen}
        onClose={onCreateRFIClose}
      >
        <RFIEntryModal
          mode={RFI_MODAL_MODE.CREATE}
          disabledFieldSet={new Set()}
          rfiType={rfiType === 'new' ? null : rfiType}
          rfiTypeList={
            rfiType === 'new' &&
            (rfiList?.length > 0 ? rfiList : Object.keys(RFI_TYPES))
          }
          requestReasonList={Object.keys(RFI_REQUEST_REASONS)}
          isLoading={isModalLoading}
          setIsLoading={setIsModalLoading}
          onClose={onCreateRFIClose}
          orderResource={orderResource}
        />
      </ModalLayout>

      {/*Reopen RFI Modal*/}
      <ModalLayout
        size="lg"
        isOpen={isReopenRFIModalOpen}
        onClose={onReopenRFIModalClose}
      >
        <RFIEntryModal
          mode={RFI_MODAL_MODE.REOPEN}
          disabledFieldSet={disabledFieldSet}
          rfiType={rfiType === 'new' ? null : rfiType}
          rfiTypeList={
            rfiType === 'new' &&
            (rfiList?.length > 0 ? rfiList : Object.keys(RFI_TYPES))
          }
          requestReasonList={Object.keys(RFI_REQUEST_REASONS)}
          isLoading={isModalLoading}
          setIsLoading={setIsModalLoading}
          onClose={onReopenRFIModalClose}
        />
      </ModalLayout>

      {/*Update RFI Modal*/}
      <ModalLayout size="lg" isOpen={isSaveRFIOpen} onClose={onSaveRFIClose}>
        <UpdateModal
          requestedItems={updatedRequestedItems}
          orderId={orderId}
          rfiType={rfiType}
          askReason={aggregateStatus}
          requestReason={data?.requestReason}
          onClose={onSaveRFIClose}
          status={getAggregateRFIStatus()}
        />
      </ModalLayout>

      {/*Update Beneficiary Modal*/}
      <ModalLayout
        isOpen={isUpdateBeneficiaryModalOpen}
        onClose={onUpdateBeneficiaryModalClose}
      >
        <DynamicInputModal
          title={"Update Beneficiary Details"}
          isOpen={isUpdateBeneficiaryModalOpen}
          onClose={onUpdateBeneficiaryModalClose}
          onSubmit={handleUpdateBeneficiaryModalSubmit}
          fields={config.updateBeneficiaryFieldsConfig(selectedBeneficiary) || []}
          isLoading={isModalLoading}
        />
      </ModalLayout>

      {/*Update User KYC Name Modal */}
      <ModalLayout isOpen={isKycUpdateOpen} onClose={onKycUpdateClose}>
        <DynamicInputModal
          title={"Update User's KYC Name"}
          isOpen={isKycUpdateOpen}
          onClose={onKycUpdateClose}
          fields={rfiKycConfig.updateUpdateKycNameFieldsConfig() || []}
          onSubmit={handleUpdateKycNameModalSubmit}
          isLoading={isModalLoading}
        />
      </ModalLayout>

      <Grid gridTemplateColumns={{ base: '1fr', lg: `7fr 0fr 3fr` }}>
        {/* Main Column */}
        <VStack
          overflowY={'hidden'}
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems={'stretch'}
          gap={8}
        >
          <HStack justify={'space-between'}>
            <Heading size="lg" color="colorPrimary">
              RFI Details
            </Heading>
            {hasAnyAuthority(
              ROLES.ADMIN_ROLE,
              ROLES.FALCON_ADMIN,
              ROLES.FALCON_OPS_MANAGER
            ) && isUserKycNameUpdatable(data) && (
              <Button
                colorScheme={'brand'}
                size="sm"
                bg="backGround"
                border={'2px solid'}
                borderColor={'whiteAlpha.500'}
                borderRadius={'lg'}
                p={4}
                onClick={onKycUpdateOpen}
                leftIcon={<MdDriveFileRenameOutline size={'1.25rem'} />}
              >
                Update KYC Name
              </Button>
            )}
          </HStack>

          {(!data || rfiType === 'new') && (
            <VStack
              overflowY={{ base: 'initial', lg: 'scroll' }}
              overflowX="hidden"
              alignItems="stretch"
              gap={8}
              px={4}
              divider={<StackDivider borderColor="whiteAlpha.300" />}
            >
              <Button
                colorScheme="brand"
                color="black"
                alignSelf={'flex-end'}
                onClick={onCreateRFIOpen}
              >
                Create RFI
              </Button>
              {rfiEnabled.data && !rfiEnabled.data?.isSupported && (
                <Alert status="warning" color={'black'} variant={'subtle'}>
                  <AlertIcon />
                  <AlertTitle>RFI Not Enabled for the User</AlertTitle>
                  <AlertDescription>
                    User:
                    <CopyableText display="inline" title="User ID">
                      {userId}
                    </CopyableText>
                    , app not updated. Minimum required app version for
                    android: {rfiEnabled.data.minimumSupportedAndroidVersion}{' '}
                    & for ios: {rfiEnabled.data.minimumSupportedIosVersion}
                  </AlertDescription>
                </Alert>
              )}
              <Text fontSize={'xl'}>
                {`RFI: ${
                  rfiType !== 'new' ? rfiType : ''
                } not yet created for order: ${orderId}`}
              </Text>
            </VStack>
          )}
          {data && rfiType !== 'new' && (
            <VStack
              overflowY={{ base: 'initial', lg: 'scroll' }}
              overflowX="hidden"
              alignItems="stretch"
              gap={8}
              px={4}
              divider={<StackDivider borderColor="whiteAlpha.300" />}
            >
              <GenericCard
                cardData={data}
                cardType={CARD_TYPE.RFI_DETAILS_CARD}
                maxW={'full'}
              />
              {updatedRequestedItems?.length > 0 && (
                <VStack gap={2} w={'full'}>
                  <HStack justify={'space-between'} w={'full'}>
                    <Heading size={'md'}>Requested Documents</Heading>
                    <HStack gap={2}>
                      {data.status === RFI_STATUS.SUBMITTED && (
                        <>
                          <Button
                            colorScheme={'red'}
                            size="sm"
                            bg="backGround"
                            border={'2px solid'}
                            borderColor={'whiteAlpha.500'}
                            borderRadius={'lg'}
                            p={4}
                            leftIcon={<MdCancel/>}
                            onClick={handleFail}
                          >
                            Fail RFI
                          </Button>
                          <Button
                            colorScheme={'brand'}
                            size="sm"
                            bg="backGround"
                            border={'2px solid'}
                            borderColor={'whiteAlpha.500'}
                            borderRadius={'lg'}
                            p={4}
                            leftIcon={<FaCheck />}
                            onClick={handleAccept}
                          >
                            Save & Accept Rest
                          </Button>
                        </>
                      )}
                      {data.status === RFI_STATUS.VERIFIED && (
                        <Button
                          colorScheme="brand"
                          variant={'outline'}
                          w={'fit-content'}
                          leftIcon={<FaRedo />}
                          justifySelf={'center'}
                          onClick={onReopenRFIModalOpen}
                        >
                          Reopen RFI
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                  <VStack w={'full'} maxH={'50vh'} overflow={'auto'} gap={2}>
                    {updatedRequestedItems.map((item) => (
                      <RequestedItem
                        item={item}
                        rfiData={data}
                        setRequestedItems={setUpdatedRequestedItems}
                        setAggregateStatus={setAggregateStatus}
                      />
                    ))}
                  </VStack>
                </VStack>
              )}
            </VStack>
          )}
        </VStack>

        <Divider
          style={{ margin: '0' }}
          borderColor="whiteAlpha.300"
          orientation="vertical"
          transition="width 0.3s ease-in-out"
        />

        {/*Right Column*/}
        <VStack
          overflowX={'hidden'}
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 2, sm: 4, lg: 8 }}
          alignItems={'stretch'}
          gap={4}
        >
          {!!userId && !!orderId && (
            <VStack w={"100%"} alignItems={"stretch"}>
              <OrderDetailsCard
                orderResource={orderResource}
                orderId={orderId}
                userId={userId}
                showNavLink={true}
                showGoBackBtn={false}
              />
            </VStack>
          )}
        </VStack>
      </Grid>
    </ContentLayout>
  )
};

export default RFIDetails;
